import { Invoice, InvoiceType } from '@/lib/types';
import { PaymentInvoiceItem, PaymentSetting } from '@/lib/types/payment';
import { roundToTwoDecimals } from '@/lib/utils/numbers';

import { getNetInvoiceAmount, getPaymentInfo } from './paymentInvoiceHelper';

export type FieldPath = Array<string | number>;

export const PAYMENT_ERROR_MAPPINGS = {
  date: 'api.payment.banner.date',
  iban: 'api.payment.banner.iban',
  bankAccount: 'api.payment.banner.bankaccount',
  toPay: 'api.payment.banner.higher',
  value: 'api.payment.banner.minimum',
} as const;

export const toFiniteNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const getCheckedInvoiceSummary = (
  invoices: PaymentInvoiceItem[] | undefined,
  checkedList: number[],
) => {
  const invoiceRows = invoices ?? [];
  const checkedInvoices = invoiceRows.filter(
    (invoice) =>
      invoice?.invoice && checkedList.includes(invoice.invoice.invoiceId),
  );
  const includedInvoices = checkedInvoices.filter(
    (invoice) => !invoice.isExcluded,
  );

  const totalValue = includedInvoices.reduce((invoiceTotal, invoice) => {
    const partialPaymentTotal = (invoice.partialPayments ?? []).reduce(
      (paymentTotal, partialPayment) =>
        paymentTotal + toFiniteNumber(partialPayment?.value),
      0,
    );

    return invoiceTotal + partialPaymentTotal;
  }, 0);

  const partialPaymentCount = includedInvoices.reduce(
    (paymentCount, invoice) =>
      paymentCount + (invoice.partialPayments?.length ?? 0),
    0,
  );

  return {
    invoiceCount: includedInvoices.length,
    partialPaymentCount,
    totalValue,
  };
};

export const getDefinedPaymentInvoices = (
  invoices: Array<PaymentInvoiceItem | undefined> | undefined,
) => {
  return (invoices ?? []).filter((invoice): invoice is PaymentInvoiceItem =>
    Boolean(invoice?.invoice),
  );
};

export const getEligibleSepaInvoices = (
  invoices: Array<PaymentInvoiceItem | undefined> | undefined,
  checkedList: number[],
) => {
  return getDefinedPaymentInvoices(invoices).filter(
    (invoice) =>
      !invoice.isExcluded &&
      (invoice.partialPayments?.length || invoice.nettingInvoices?.length) &&
      checkedList.includes(invoice.invoice.invoiceId),
  );
};

export const exceedsSepaBatchLimit = (invoices: PaymentInvoiceItem[]) => {
  const partialPaymentCount = invoices.reduce(
    (amount, invoice) => amount + invoice.partialPayments.length,
    0,
  );

  return invoices.length > 75 || partialPaymentCount > 75;
};

export const getSelectedIndexes = (
  formInvoices: PaymentInvoiceItem[] = [],
  checkedList: number[],
) => {
  return formInvoices
    .map((invoice, index) =>
      invoice?.invoice && checkedList.includes(invoice.invoice.invoiceId)
        ? index
        : null,
    )
    .filter((index): index is number => index !== null);
};

export const buildValidationPaths = (
  invoiceIndexes: number[],
  includeIban: boolean,
  formInvoices: PaymentInvoiceItem[] = [],
) => {
  const fieldPaths: FieldPath[] = includeIban ? [['iban']] : [];

  invoiceIndexes.forEach((index) => {
    const invoice = formInvoices[index];
    if (!invoice) {
      return;
    }

    fieldPaths.push(['invoices', index, 'toPay']);

    (invoice.partialPayments ?? []).forEach((_, paymentIndex) => {
      fieldPaths.push(
        ['invoices', index, 'partialPayments', paymentIndex, 'value'],
        ['invoices', index, 'partialPayments', paymentIndex, 'bankAccount'],
        ['invoices', index, 'partialPayments', paymentIndex, 'date'],
      );
    });
  });

  return fieldPaths;
};

export const getValidationIndexes = (
  selectedIndexes: number[],
  requestedIndexes?: number[],
) => {
  if (!requestedIndexes?.length) {
    return selectedIndexes;
  }

  return requestedIndexes.filter((index) => selectedIndexes.includes(index));
};

export const extractChangedInvoiceIndexes = (
  changedFields: Array<{ name: Array<string | number> }>,
) => {
  return Array.from(
    new Set<number>(
      changedFields.flatMap((field) => {
        const { name } = field;

        if (
          Array.isArray(name) &&
          name[0] === 'invoices' &&
          typeof name[1] === 'number'
        ) {
          return [name[1]];
        }

        return [];
      }),
    ),
  );
};

export const hasIbanFieldChanged = (
  changedFields: Array<{ name: Array<string | number> }>,
) => {
  return changedFields.some(
    (field) => Array.isArray(field.name) && field.name[0] === 'iban',
  );
};

const getPaymentSettingForInvoice = (
  invoice: Invoice,
  paymentSettings: PaymentSetting[],
): PaymentSetting | undefined => {
  const counterRelationId = invoice.counterRelationId;
  if (!counterRelationId) {
    return;
  }

  return paymentSettings.find(
    (paymentSetting) => paymentSetting.counterRelationId === counterRelationId,
  );
};

export const createPaymentInvoices = (
  invoices: Invoice[],
  existingInvoices: PaymentInvoiceItem[] = [],
  paymentSettings: PaymentSetting[] = [],
): {
  paymentInvoices: PaymentInvoiceItem[];
  unusedCreditInvoices: Invoice[];
} => {
  const excludedInvoices = existingInvoices.filter(
    (invoice) => invoice.isExcluded,
  );
  const isExcluded = (invoiceId: number) => {
    return excludedInvoices.some(
      (invoice) => invoice.invoice.invoiceId === invoiceId,
    );
  };

  const purchaseInvoices = invoices
    .filter(
      (invoice) =>
        invoice.type.toString() === InvoiceType[InvoiceType.Purchase],
    )
    .sort(
      (a, b) =>
        new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime(),
    );

  let creditInvoices = invoices.filter(
    (invoice) =>
      invoice.type.toString() === InvoiceType[InvoiceType.CreditNote],
  );

  const paymentInvoices: PaymentInvoiceItem[] = [];

  for (const invoice of purchaseInvoices) {
    let restAmount = invoice.amount;
    const nettingInvoices: Invoice[] = [];

    for (const creditInvoice of creditInvoices) {
      if (creditInvoice.counterRelationId !== invoice.counterRelationId) {
        continue;
      }

      const absCreditAmount = Math.abs(creditInvoice.amount);
      if (restAmount >= absCreditAmount) {
        nettingInvoices.push(creditInvoice);
        restAmount -= absCreditAmount;
      }
    }

    creditInvoices = creditInvoices.filter(
      (creditInvoice) =>
        !nettingInvoices.some(
          (nettingInvoice) =>
            nettingInvoice.invoiceId === creditInvoice.invoiceId,
        ),
    );

    const paymentSetting = getPaymentSettingForInvoice(
      invoice,
      paymentSettings,
    );
    const paymentInfo = getPaymentInfo(invoice, paymentSetting);
    const netInvoiceAmount = getNetInvoiceAmount(invoice, nettingInvoices);
    const discountedValue = paymentInfo.discount
      ? roundToTwoDecimals((invoice.amount / 100) * paymentInfo.discount)
      : 0;
    const initialPayment = roundToTwoDecimals(
      netInvoiceAmount - discountedValue,
    );

    paymentInvoices.push({
      invoice,
      nettingInvoices,
      isExcluded: isExcluded(invoice.invoiceId),
      paymentSetting,
      receiverName: invoice.supplier.name,
      paymentTerm: paymentInfo.paymentTerm,
      discount: paymentInfo.discount,
      netInvoiceAmount,
      toPay: initialPayment,
      partialPayments: [
        {
          ...paymentInfo,
          value: initialPayment,
        },
      ],
    });
  }

  return {
    paymentInvoices,
    unusedCreditInvoices: creditInvoices,
  };
};
